import { config } from '@/lib/config';
import { LOST_MS, STALE_MS } from '@/lib/staleness/watchdog';
import type { MapObject } from '../types';

export type StaleState = 'fresh' | 'dim' | 'lost';
/** Aircraft desaturate after this age (matches the Aircraft layer's >30s). */
const AIRCRAFT_DIM_MS = 30_000;
/** Aircraft + dots are removed after this age (reuses the global staleMs). */
const REMOVE_MS = config.aircraft.staleMs; // 120_000

export interface LiveEntry {
  obj: MapObject;
  lastSeen: number;
}
export type LiveStore = Map<string, LiveEntry>;
export type LiveObject = MapObject & { staleState: StaleState };

/** Upsert objects by id; latest position + lastSeen win. */
export function upsertObjects(store: LiveStore, objs: MapObject[], nowMs: number): void {
  for (const obj of objs) store.set(obj.id, { obj, lastSeen: nowMs });
}

/** Apply per-kind staleness (spec §6). Mutates `store` to drop removed entries.
 *  Returns the survivors annotated with staleState. */
export function sweepObjects(store: LiveStore, nowMs: number): LiveObject[] {
  const out: LiveObject[] = [];
  for (const [id, entry] of store) {
    const age = nowMs - entry.lastSeen;
    if (entry.obj.kind === 'drone') {
      const staleState: StaleState = age >= LOST_MS ? 'lost' : age >= STALE_MS ? 'dim' : 'fresh';
      out.push({ ...entry.obj, staleState }); // drones never removed
    } else if (entry.obj.kind === 'aircraft') {
      if (age > REMOVE_MS) {
        store.delete(id);
        continue;
      }
      out.push({ ...entry.obj, staleState: age > AIRCRAFT_DIM_MS ? 'dim' : 'fresh' });
    } else {
      if (age > REMOVE_MS) {
        store.delete(id);
        continue;
      }
      out.push({ ...entry.obj, staleState: 'fresh' });
    }
  }
  return out;
}
